import React, { useState, useEffect, useRef } from 'react';
import { X, Copy, CheckCircle2, AlertCircle, Clock } from 'lucide-react';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  orderId: string;
  qrUrl: string;
  amount: number;
  accountInfo: {
    accountNo: string;
    bankName: string;
    accountName: string;
  };
  timeout?: number; // seconds, default 600 (10 minutes)
}

type PaymentStatus = 'pending' | 'completed' | 'expired' | 'failed';

export default function PaymentModal({
  isOpen,
  onClose,
  onSuccess,
  orderId,
  qrUrl,
  amount,
  accountInfo,
  timeout = 600 // 10 minutes
}: PaymentModalProps) {
  const [status, setStatus] = useState<PaymentStatus>('pending');
  const [countdown, setCountdown] = useState<number>(timeout);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Format countdown as MM:SS
  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format currency
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  // Check payment status (polling)
  const checkPaymentStatus = async () => {
    try {
      // Get API URL from environment
      const MODAL_API_URL = import.meta.env.VITE_MODAL_API_BASE_URL || '';

      const response = await fetch(`${MODAL_API_URL}/payment/status/${orderId}`);

      if (!response.ok) {
        console.error('Failed to check payment status:', response.statusText);
        return;
      }

      const data = await response.json();
      const newStatus = data.status as PaymentStatus;

      // Update status
      if (newStatus !== status) {
        setStatus(newStatus);

        // Handle completed status
        if (newStatus === 'completed') {
          // Stop polling and countdown
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }

          // Auto-close after 1.5s and call onSuccess
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 1500);
        }

        // Stop polling for terminal statuses
        if (['completed', 'expired', 'failed'].includes(newStatus)) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
        }
      }
    } catch (error) {
      console.error('Poll error:', error);
    }
  };

  // Start polling and countdown when modal opens
  useEffect(() => {
    if (!isOpen || !orderId) return;

    // Reset state
    setStatus('pending');
    setCountdown(timeout);
    setCopiedField(null);

    // Start polling (every 3 seconds)
    pollIntervalRef.current = setInterval(() => {
      checkPaymentStatus();
    }, 3000);

    // Initial check
    checkPaymentStatus();

    // Start countdown timer
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        const newValue = prev - 1;

        // When countdown reaches 0, mark as expired
        if (newValue <= 0) {
          setStatus('expired');

          // Stop intervals
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }

          // Final check in case webhook arrived late
          checkPaymentStatus();

          return 0;
        }

        return newValue;
      });
    }, 1000);

    // Cleanup on unmount or modal close
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [isOpen, orderId, timeout]);

  // Status display config
  const statusConfig = {
    pending: {
      text: 'Đang chờ thanh toán',
      icon: <Clock className="w-5 h-5 text-yellow-600 animate-pulse" />,
      color: 'text-yellow-600'
    },
    completed: {
      text: 'Thanh toán thành công',
      icon: <CheckCircle2 className="w-5 h-5 text-green-600" />,
      color: 'text-green-600'
    },
    expired: {
      text: 'Hết hạn thanh toán',
      icon: <AlertCircle className="w-5 h-5 text-gray-500" />,
      color: 'text-gray-500'
    },
    failed: {
      text: 'Thanh toán thất bại',
      icon: <AlertCircle className="w-5 h-5 text-red-600" />,
      color: 'text-red-600'
    }
  };

  const currentStatus = statusConfig[status];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            Thanh toán qua VietQR
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* QR Code */}
          <div className="flex flex-col items-center mb-4">
            <img
              src={qrUrl}
              alt="VietQR Code"
              className="w-64 h-64 border-2 border-gray-200 rounded-lg"
            />
          </div>

          {/* Payment Info */}
          <div className="mb-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Số tiền:</span>
              <span className="text-lg font-bold text-green-600">
                {formatCurrency(amount)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Số tài khoản:</span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{accountInfo.accountNo}</span>
                <button
                  onClick={() => copyToClipboard(accountInfo.accountNo, 'account')}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Copy số tài khoản"
                >
                  {copiedField === 'account' ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Ngân hàng:</span>
              <span className="text-sm font-medium">{accountInfo.bankName}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Tên chủ TK:</span>
              <span className="text-sm font-medium">{accountInfo.accountName}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Nội dung CK:</span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{orderId}</span>
                <button
                  onClick={() => copyToClipboard(orderId, 'orderId')}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Copy nội dung chuyển khoản"
                >
                  {copiedField === 'orderId' ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-600" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-2">
              {currentStatus.icon}
              <span className={`font-semibold ${currentStatus.color}`}>
                {currentStatus.text}
              </span>
            </div>

            {status === 'pending' && (
              <div className="text-center text-sm text-gray-600">
                Thời gian còn lại: <span className="font-mono font-bold">{formatCountdown(countdown)}</span>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-900 mb-2">
              Hướng dẫn thanh toán:
            </p>
            <ol className="list-decimal pl-5 text-sm text-blue-800 space-y-1">
              <li>Mở app ngân hàng, chọn quét mã QR hoặc chuyển khoản thủ công</li>
              <li>Nhập <strong>đúng số tiền</strong> và <strong>nội dung chuyển khoản</strong></li>
              <li>Hoàn tất giao dịch</li>
              <li>Chờ xác nhận tự động (không cần reload trang)</li>
            </ol>
          </div>

          {/* Action Buttons */}
          {status === 'completed' && (
            <button
              onClick={() => {
                onSuccess();
                onClose();
              }}
              className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition"
            >
              Đóng
            </button>
          )}

          {(status === 'expired' || status === 'failed') && (
            <button
              onClick={onClose}
              className="w-full px-4 py-3 bg-gray-400 text-white rounded-lg hover:bg-gray-500 font-medium transition"
            >
              Đóng
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
